import { useCallback, useEffect, useState } from 'react';
import type { PuntoMedicion, SerieMedicion } from '../../api/ops/web';
import { EstadoCargando, EstadoError, EstadoVacia } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import { Segmentos } from '../../componentes/Segmentos';
import { estadoDeError } from '../../logica/estados';
import { cifra, diaMes } from '../../logica/fechasCortas';
import { useCabecera } from '../../marco/cabecera';
import { usePermisos } from '../../permisos/permisos';
import type { PropsPantalla } from '../tipos';
import { PERIODOS, rangoDe, variacion, type Periodo } from './reglas';
import { AYUDA_MEDICION } from './textos';

/**
 * «Medición» (13.5): impresiones, clics y conversiones netas por día (o por mes en 12 meses) frente al periodo anterior
 * (`getWebMetrics`, sin cookies), con el total de bloqueos del periodo (`totals.blocks`, G634) para todos. Con
 * `web.medicion.detalle` (Propietario y Administrador, G616): «De dónde llegan» (la web y cada widget). El Encargado y el
 * Socio solo ven las cifras sumadas. Las conversiones no se desglosan por dominio (revisión §5.10).
 */
type Metrica = 'impressions' | 'clicks' | 'net_conversions';
const METRICAS: readonly { valor: Metrica; texto: string; unidad: string }[] = [
    { valor: 'impressions', texto: 'Impresiones', unidad: 'impresiones' },
    { valor: 'clicks', texto: 'Clics', unidad: 'clics' },
    { valor: 'net_conversions', texto: 'Conversiones', unidad: 'conversiones' },
];

interface Datos {
    actual: SerieMedicion;
    anterior: SerieMedicion;
    origen: { nombre: string; valor: number }[] | null;
    /** Total del periodo (`MetricPoint.blocks`, G634): también para el Encargado y el Socio, sin desglose. */
    bloqueos: number;
}

export function Medicion({ ops, negocio, movil }: PropsPantalla) {
    const { puede } = usePermisos();
    const detalle = puede('web.medicion.detalle');
    const [periodo, setPeriodo] = useState<Periodo>('30');
    const [metrica, setMetrica] = useState<Metrica>('impressions');
    const [tabla, setTabla] = useState(false);
    const [datos, setDatos] = useState<{ clave: string; datos: Datos } | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);
    const clave = `${periodo}|${String(intento)}`;

    useEffect(() => {
        let vivo = true;
        const r = rangoDe(periodo);
        Promise.all([
            ops.getWebMetrics(negocio, { ...r.actual, granularity: r.granularity }),
            ops.getWebMetrics(negocio, { ...r.anterior, granularity: r.granularity }),
            detalle ? ops.getWebMetrics(negocio, { ...r.actual, granularity: 'month', breakdown: 'widget' }).catch(() => null) : Promise.resolve(null),
            detalle ? ops.getWebMetrics(negocio, { ...r.actual, granularity: 'month', breakdown: 'channel' }).catch(() => null) : Promise.resolve(null),
            detalle && puede('web.widgets.gestionar') ? ops.listWidgets(negocio, { limit: 100 }).catch(() => null) : Promise.resolve(null),
        ])
            .then(([actual, anterior, porWidget, porCanal, widgets]) => {
                if (!vivo) {
                    return;
                }
                let origen: Datos['origen'] = null;
                if (porWidget !== null && porCanal !== null) {
                    const suma = new Map<string, number>();
                    for (const p of porWidget.data) {
                        if (p.widget_id != null) {
                            suma.set(p.widget_id, (suma.get(p.widget_id) ?? 0) + p.impressions);
                        }
                    }
                    const web = porCanal.data.filter((p) => p.channel === 'web').reduce((t, p) => t + p.impressions, 0);
                    const nombre = (id: string) => widgets?.data.find((w) => w.id === id)?.name ?? 'Widget';
                    origen = [{ nombre: 'Tu web', valor: web }, ...[...suma.entries()].map(([id, valor]) => ({ nombre: nombre(id), valor }))].sort((a, b) => b.valor - a.valor);
                }
                setDatos({ clave, datos: { actual, anterior, origen, bloqueos: actual.totals.blocks } });
                setError(null);
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, periodo, detalle, puede, clave]);

    useCabecera({ ayuda: AYUDA_MEDICION, acciones: [] });

    if (error !== null) {
        return <EstadoError seccion="la medición" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={recargar} movil={movil} />;
    }
    const d = datos?.clave === clave ? datos.datos : null;
    const textoPeriodo = PERIODOS.find((p) => p.valor === periodo)?.texto ?? '';

    return (
        <div className="rv-ca-apartado rv-wb-medicion">
            {!detalle && (
                <p className="rv-ca-nota rv-ca-nota--gris">
                    <Icono nombre="eye" />
                    <span>Con tu rol ves las cifras sumadas de la web y los widgets, sin el detalle de cada uno.</span>
                </p>
            )}
            <div className="rv-wb-periodo">
                <Segmentos etiqueta="Periodo" movil={movil} items={PERIODOS.map((p) => ({ etiqueta: p.texto, icono: 'calendar' }))} valor={PERIODOS.findIndex((p) => p.valor === periodo)} onCambio={(i) => { setPeriodo(PERIODOS[i]?.valor ?? '30'); }} />
            </div>
            {d === null ? (
                <EstadoCargando seccion="la medición" />
            ) : d.actual.totals.impressions === 0 && d.actual.totals.clicks === 0 && d.anterior.totals.impressions === 0 ? (
                <EstadoVacia seccion="Medición" />
            ) : (
                <>
                    <div className={`rv-wb-graficos${d.origen === null ? ' rv-wb-graficos--solo' : ''}`}>
                        <section className="rv-ca-lista rv-wb-caja" aria-label={`${METRICAS.find((m) => m.valor === metrica)?.texto ?? ''} por día`}>
                            <Grafico actual={d.actual.data} anterior={d.anterior.data} metrica={metrica} periodo={textoPeriodo} tabla={tabla} total={d.actual.totals[metrica]} totalAnterior={d.anterior.totals[metrica]} />
                            <button type="button" className="rv-wb-enlace" onClick={() => { setTabla((t) => !t); }}>
                                <Icono nombre="table" /> {tabla ? 'Ver como gráfico' : 'Ver como tabla'}
                            </button>
                        </section>
                        {d.origen !== null && (
                            <section className="rv-ca-lista rv-wb-caja" aria-label="De dónde llegan">
                                <strong>De dónde llegan</strong>
                                <small>{`Impresiones por sitio · ${textoPeriodo.toLocaleLowerCase('es')}`}</small>
                                {d.origen.map((o) => {
                                    const total = d.origen?.reduce((t, x) => t + x.valor, 0) ?? 0;
                                    const pc = total === 0 ? 0 : Math.round((o.valor / total) * 100);
                                    return (
                                        <div key={o.nombre} className="rv-wb-origen">
                                            <span>{o.nombre}</span>
                                            <strong>{cifra(o.valor)}</strong>
                                            <small>{`${String(pc)} %`}</small>
                                            <i style={{ width: `${String(pc)}%` }} aria-hidden="true" />
                                        </div>
                                    );
                                })}
                            </section>
                        )}
                    </div>
                    <div className="rv-wb-tarjetas">
                        {METRICAS.map((m) => {
                            const v = variacion(d.actual.totals[m.valor], d.anterior.totals[m.valor]);
                            const pie =
                                m.valor === 'impressions'
                                    ? 'Veces que se ha mostrado'
                                    : m.valor === 'clicks'
                                      ? `${d.actual.totals.impressions === 0 ? '0' : ((d.actual.totals.clicks / d.actual.totals.impressions) * 100).toLocaleString('es-ES', { maximumFractionDigits: 1 })} % de las impresiones`
                                      : `Reservas, citas y entradas · ${cifra(d.actual.totals.conversions_reversed)} anuladas`;
                            return (
                                <button key={m.valor} type="button" aria-pressed={metrica === m.valor} className={`rv-wb-tarjeta${metrica === m.valor ? ' rv-wb-tarjeta--on' : ''}`} onClick={() => { setMetrica(m.valor); }}>
                                    <strong>{m.texto}</strong>
                                    <span>
                                        <b>{cifra(d.actual.totals[m.valor])}</b> {v !== null && <em>{v}</em>}
                                    </span>
                                    <small>{pie}</small>
                                </button>
                            );
                        })}
                        <div className="rv-wb-tarjeta">
                            <strong>Bloqueos</strong>
                            <span>
                                <b>{cifra(d.bloqueos)}</b>
                            </span>
                            <small>Veces que no se ha mostrado</small>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

/** Línea del periodo y, discontinua, la del anterior (§21). Con «Ver como tabla», las cifras por día. */
function Grafico({ actual, anterior, metrica, periodo, tabla, total, totalAnterior }: { actual: PuntoMedicion[]; anterior: PuntoMedicion[]; metrica: Metrica; periodo: string; tabla: boolean; total: number; totalAnterior: number }) {
    const unidad = METRICAS.find((m) => m.valor === metrica)?.unidad ?? '';
    const v = variacion(total, totalAnterior);
    const valores = actual.map((p) => p[metrica]);
    const previos = anterior.map((p) => p[metrica]);
    const maximo = Math.max(1, ...valores, ...previos);
    const ancho = 640;
    const alto = 200;
    const linea = (serie: number[]) =>
        serie.map((y, i) => `${i === 0 ? 'M' : 'L'}${((i / Math.max(1, serie.length - 1)) * ancho).toFixed(1)},${(alto - (y / maximo) * alto).toFixed(1)}`).join(' ');
    return (
        <>
            <div className="rv-wb-cifra">
                <b>{cifra(total)}</b> <span>{unidad}</span> {v !== null && <em>{v}</em>}
            </div>
            <small>{`${periodo} frente al periodo anterior`}</small>
            {tabla ? (
                <table className="rv-wb-tabla">
                    <thead>
                        <tr>
                            <th scope="col">Día</th>
                            <th scope="col">{unidad}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {actual.map((p) => (
                            <tr key={p.period_start}>
                                <td>{diaMes(`${p.period_start}T12:00:00`)}</td>
                                <td>{cifra(p[metrica])}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            ) : (
                <svg className="rv-wb-grafico" viewBox={`0 0 ${String(ancho)} ${String(alto)}`} preserveAspectRatio="none" role="img" aria-label={`${cifra(total)} ${unidad}`}>
                    {previos.length > 1 && <path d={linea(previos)} className="rv-wb-grafico__antes" />}
                    {valores.length > 1 && <path d={linea(valores)} className="rv-wb-grafico__ahora" />}
                </svg>
            )}
        </>
    );
}
